import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Check, ContentCopy, Share } from "@mui/icons-material";
import { copyToClipboard, getTranslation } from "common";
import { useTranslations } from "hooks";

/**
 * The "Share chat" link dialog, shared by <AiDrawer /> and the AI Assistant
 * page: a spinner while the backend mints the slug, then a read-only field
 * with the public URL and a copy button.
 *
 * It lives here rather than in each surface so the two stay identical; the
 * clipboard quirks are handled by `copyToClipboard` (see common/).
 *
 * The dialog is a pure view: the caller owns the URL and its loading state,
 * because minting the link is an authenticated POST that belongs to the hook
 * driving that surface's sessions (useLLM / useAiStudioChat).
 */

const ROUND_FIELD = "20px";
const ROUND_BUTTON = "16px";
const ROUND_ICON = "14px";

/** How long the button stays on the green check after a copy. */
const COPIED_FEEDBACK_MS = 1500;

interface ShareChatDialogProps {
  open: boolean;
  /** Public URL to show; empty while `loading` is true. */
  url: string;
  loading: boolean;
  onClose: () => void;
}

const ShareChatDialog: React.FC<ShareChatDialogProps> = ({
  open,
  url,
  loading,
  onClose,
}) => {
  const translations = useTranslations();
  const t = (key: string, fallback: string) =>
    getTranslation(translations, key) || fallback;

  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<number | null>(null);

  const clearCopyTimer = () => {
    if (copyTimerRef.current) {
      window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = null;
    }
  };

  useEffect(() => clearCopyTimer, []);

  // A new link (or a reopened dialog) must not inherit the previous copy's
  // green check, or it reads as "already copied" for a URL nobody copied.
  useEffect(() => {
    setCopied(false);
    clearCopyTimer();
  }, [url, open]);

  const handleCopy = () => {
    copyToClipboard(url);
    setCopied(true);
    clearCopyTimer();
    copyTimerRef.current = window.setTimeout(() => {
      setCopied(false);
      copyTimerRef.current = null;
    }, COPIED_FEEDBACK_MS);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: ROUND_FIELD } }}
    >
      <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <Share color="primary" />
        {t("ai_drawer.share_chat_title", "Condividi chat")}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          {t(
            "ai_drawer.share_chat_description",
            "Chiunque abbia questo link potrà leggere la conversazione."
          )}
        </Typography>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <TextField
            fullWidth
            size="small"
            value={url}
            inputProps={{
              readOnly: true,
              onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                e.currentTarget.select(),
            }}
            InputProps={{
              sx: {
                borderRadius: ROUND_FIELD,
                fontFamily: "monospace",
                fontSize: "0.85rem",
              },
              endAdornment: (
                <Tooltip
                  title={
                    copied
                      ? t("ai_drawer.copied", "Copiato")
                      : t("ai_drawer.copy_link", "Copia link")
                  }
                  placement="top"
                >
                  <IconButton
                    size="small"
                    onClick={handleCopy}
                    sx={{
                      borderRadius: ROUND_ICON,
                      color: copied ? "success.main" : "text.secondary",
                      "&:hover": { color: "primary.main" },
                    }}
                  >
                    {copied ? (
                      <Check fontSize="small" />
                    ) : (
                      <ContentCopy fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              ),
            }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          sx={{ borderRadius: ROUND_BUTTON, textTransform: "none" }}
        >
          {t("generic.button.cancel", "Annulla")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareChatDialog;
